import { Fragment, useContext, useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"

import { ChartsContainer } from "../../../components/graphs/ChartsContainer"
import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../../services/KonanAPI"
import { InsightList } from "../../../types/generated/api/InsightList"
import { customRechartsColorScheme } from "../../../utils/genericHelpers"
import { ReportContext } from "../Context"
import type { PredictionsPerEngineProps, PredictionsPerWorkflowProps } from "../interfaces"
import { DecisionsPerWorkflowItem } from "./DecisionsPerWorkflowItem"

/**
 * PredictionsPerEngine Component
 *
 * Visualizes the distribution of predictions across different workflows with an
 * adaptive layout that ensures all workflows are visible regardless of their relative sizes.
 *
 * Key features:
 * - Automatically adjusts visualization widths to ensure visibility of small percentages
 * - Maintains proportional relationships between workflows
 * - Provides detailed statistics for each workflow
 * - Includes timestamp information for workflow execution
 *
 * @param {InsightProps} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Grid item containing the workflow visualization
 */
export const PredictionsPerEngine = (props: PredictionsPerEngineProps): React.ReactElement => {
  const { insight, engine, isFeedback } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { report } = useContext(ReportContext)

  const { t } = useTranslation("insights")

  // Fetch labels insight data from the API
  const { data: insightData, isLoading: isLoadingInsightData } = useQuery<
    AxiosResponse<PredictionsPerWorkflowProps>,
    AxiosError
  >(
    ["predictions-per-workflow", projectId, report?.uuid, insight.uuid],
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  // Processes and formats workflow data for visualization.
  const PredictionsCounts =
    insightData?.data.data.length && insightData?.data.data.length > 4
      ? insightData?.data.data.slice(0, 3).concat([
          {
            name: t("PredictionsPerEngine.other"),
            count: insightData?.data.data.slice(3).reduce((totalCount, workflow) => workflow.count + totalCount, 0),
            active_intervals: [],
          },
        ])
      : insightData?.data.data

  const adjustedData = useMemo(() => {
    // Return empty array if data is still loading to prevent processing undefined data
    if (isLoadingInsightData) return []

    // Initialize overflow to track excess width from small percentages
    let overflow = 0

    const result = PredictionsCounts?.map((workflow) => {
      // Calculate the percentage this workflow represents of total count
      const percent = Number(((workflow.count / (insightData?.data.count ?? 1)) * 100).toFixed(2))

      // Calculate the maximum width any single item can have while ensuring all items are visible
      const maxItemWidth = 100 - (PredictionsCounts.length - 1) * 2

      // Function to determine the appropriate width for this workflow
      const getWidth = (): number => {
        if (2 < percent && percent <= maxItemWidth) {
          // Normal range: use percentage directly
          return percent
        } else if (percent > maxItemWidth) {
          // Exceeds maximum: cap at maxItemWidth
          return maxItemWidth
        } else if (percent < 2) {
          // Very small percentage: ensure minimum visibility
          const itemWidth = Math.max((100 - maxItemWidth) * percent, 2)
          // Track overflow for redistribution
          if (itemWidth > 2) overflow += itemWidth - 2
          return itemWidth
        } else {
          // Fallback case for edge conditions
          return 2
        }
      }

      // Return the workflow data with calculated percent and width
      return { ...workflow, percent: percent, width: getWidth() }
    })

    // Redistribute overflow to the first workflow if any exists
    // This ensures total width remains 100%
    if (overflow > 0 && result?.[0]?.width) {
      result[0].width -= overflow
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightData?.data.count, insightData?.data.data])

  const GraphStateSwitch = (): React.ReactElement => {
    if (isLoadingInsightData || (adjustedData?.length && adjustedData?.length > 0))
      return (
        <Grid container pt={2} height={"100%"} alignContent={"space-between"}>
          <Grid item xs={12} display={"flex"} gap={0.25}>
            {adjustedData?.map((workflow, index) => (
              <DecisionsPerWorkflowItem
                width={100 / adjustedData.length}
                workflow={workflow}
                key={workflow.name}
                chipColor={customRechartsColorScheme()[index % 7]?.border ?? ""}
              />
            ))}
          </Grid>

          <Grid container gap={0.25} display={"flex"} p={0} wrap="nowrap">
            {adjustedData?.map((workflow, index) => (
              <Grid item key={`bar-${workflow.name}`} width={`${workflow.width}%`}>
                <hr
                  style={{
                    background: customRechartsColorScheme()[index % 7]?.border,
                    height: "8px",
                    margin: "0px",
                    border: "0px",
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )

    return (
      <Grid container item xs={12} justifyContent={"center"} alignItems={"center"}>
        <KonanEmptyState
          title={
            insight.status !== InsightList.status.SUCCEEDED
              ? `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`
              : t("PredictionsPerEngine.unavailable")
          }
        />
      </Grid>
    )
  }

  return (
    <Grid item xs={12} sm={12} lg={8}>
      <ChartsContainer
        title={`${t("PredictionsPerEngine.type." + (isFeedback ? "feedback" : "decision"))} ${t(
          "PredictionsPerEngine.title",
          {
            engine: t("engineTypes." + engine.toLowerCase(), {
              defaultValue: engine,
            }),
          },
        )}`}
        tooltipText={
          <Fragment>
            {t("PredictionsPerEngine.tooltip", {
              dataType: t("PredictionsPerEngine.type." + (isFeedback ? "feedback" : "decision")).toLowerCase(),
            })}
          </Fragment>
        }
        isLoading={isLoadingInsightData}
        graphHeight={150}
      >
        {GraphStateSwitch()}
      </ChartsContainer>
    </Grid>
  )
}
